import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Accordion,
  Card,
  Button,
  Table,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FaCheckCircle, FaSync } from "react-icons/fa";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import { importTestData, resetTest, sendTestReports, updateTest } from "../../../api/lead";

const ExamEditPage = () => {
  const { state } = useLocation();
  const { exam } = state || {};
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [message, setMessage] = useState("");

  // FORM STATE
  const [formData, setFormData] = useState(() => {
    if (!exam) {
      return {};
    }

    const formatDate = (obj) =>
      obj?.$date ? new Date(obj.$date).toISOString().slice(0, 16) : "";

    return {
      name: exam.name,
      description: exam.description,
      subject: exam.subject,
      duration: exam.duration,
      difficulty: exam.difficulty,
      classes_enabled: exam.classes_enabled,
      registration_start_date: formatDate(exam.registration_start_date),
      start_time: formatDate(exam.start_time),
      end_time: formatDate(exam.end_time),
      content_type: exam.content_type,
    };
  });

  if (!exam) return <h4 className="text-danger">No Exam Data Found</h4>;

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = exam.user_tests.map((u) => u.user_id);
      setSelectedUsers(allIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const formatDate = (obj) =>
    obj?.$date ? new Date(obj.$date).toISOString().slice(0, 16) : "";

  const attemptedCount = (t) => t.answers?.length || 0;

  const correctCount = (t) =>
    t.answers?.filter((a) => a.is_correct === 1).length || 0;

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid .xlsx or .csv file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("test_id", exam.id);
    alert("Data will be uploaded. Please Check back after 1 min");
    try {
      const response = await importTestData(formData);
      if (response.status != true) {
        alert(response.message);
        window.location.reload();
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      alert("Failed to import data.");
    }
  };
  // FORM CHANGE HANDLER
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "classes_enabled") {
      setFormData({
        ...formData,
        classes_enabled: value.split(",").map((x) => parseInt(x.trim())),
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleResetTest = async (user_id) => {
    let data = {
      test_id: exam.id,
      user_id: user_id
    }

    try {
      const response = await resetTest(data);
      if (response.status === true) {
        alert(response.message)
      } else {
        alert("Error: " + response.message)
      }
    } catch(error) {
      throw error
    }
  };

  const handleUpdate = async () => {
    let data = {
      test_id: exam.id,
      ...formData
    }

    try {
      const response = await updateTest(data)
      if (response.status === true) {
        alert(response.message)
      } else {
        alert("Error: " + response.message)
      }
    } catch(error) {
      throw error
    }
  };

  const handleReportSend = async (action, user_id) => {
    let data = {}
    if(action == 1) {
      console.log('sending report to all: ', selectedUsers)
      data['test_id'] = exam.id
      data['user_list'] = selectedUsers
    } else {
      console.log('sending report to just one: ', user_id)
      data['test_id'] = exam.id
      data['user_list'] = [user_id]
    }

    try {
      const response = await sendTestReports(data);
      if (response.status === true) {
        alert(response.message)
      } else {
        alert("Error: " + response.message)
      }
    } catch(error) {
      throw error
    }
  }

  return (
    <div className="container mt-4">
      {/* BACK BUTTON */}
      <div className="mb-4">
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <span className="mx-4 h5">Edit {formData.name}</span>
      </div>

      {message && (
        <div className="alert alert-success d-flex align-items-center rounded-3 mb-4">
          <FaCheckCircle className="flex-shrink-0 me-2" size={20} />
          <div>{message}</div>
        </div>
      )}

      <div className="row">
        <div className="col-7">
          <form>
            <div className="mb-3">
              <label className="form-label fw-semibold">Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Subject</label>
                <input
                  className="form-control"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Duration</label>
                <input
                  className="form-control"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Difficulty</label>
                <input
                  className="form-control"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">
                  Classes Enabled (comma separated)
                </label>
                <input
                  className="form-control"
                  name="classes_enabled"
                  value={formData.classes_enabled.join(", ")}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold">
                  Registration Start
                </label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="registration_start_date"
                  value={formData.registration_start_date}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold">Start Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold">End Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Content Type</label>
              <input
                className="form-control"
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
              />
            </div>

            <button
              type="button"
              className="btn primary-button text-white rounded-pill w-100 mt-3"
              onClick={handleUpdate}
            >
              Update Test
            </button>
          </form>
        </div>

        <div className="col-5">
          <div className="row g-3">
            <h4 className="mb-3">Test Summary</h4>
            <div className="col-sm-6">
              <div className="card p-3">
                <h6 className="mb-0">Registered Users</h6>
                <p className="mb-0 fs-5 fw-bold">{exam.user_tests.length}</p>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="card p-3">
                <h6 className="mb-0">Completed By</h6>
                <p className="mb-0 fs-5 fw-bold">
                  {exam.user_tests.filter((u) => u.test_completed === 1).length}
                </p>
              </div>
            </div>
          </div>

          {/* TEST QUESTIONS SECTION */}
          <div className="mt-4">
            <h4 className="mb-3">Test Questions</h4>

            {exam.test_data.length === 0 ? (
              <div className="text-muted d-flex align-items-center justify-content-center">
                <span>No questions available for this test.</span>
                <button
                  type="button"
                  className="btn primary-button text-white rounded-pill mx-2"
                  onClick={handleImport}
                >
                  Import Test
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx, .csv"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
            ) : (
              <div
                style={{
                  maxHeight: "56vh", // adjust as needed
                  overflowY: "auto",
                  paddingRight: "10px",
                }}
              >
                <Accordion defaultActiveKey="">
                  {exam.test_data[0].questions.map((q, index) => (
                    <Accordion.Item eventKey={index.toString()} key={q.id}>
                      <Accordion.Header>
                        {index + 1}.{" "}
                        {q.question ? q.question : "Image Question"}
                      </Accordion.Header>
                      <Accordion.Body>
                        {/* Show question image if available */}
                        {q.question_img && (
                          <img
                            src={q.question_img}
                            alt={`Question ${index + 1}`}
                            style={{ maxWidth: "100%", marginBottom: "1rem" }}
                          />
                        )}

                        <ul className="list-group list-group-flush">
                          {q.options.map((opt) => (
                            <li
                              key={opt.id}
                              className={`list-group-item ${
                                opt.id === q.correct_option_id
                                  ? "bg-success text-white fw-bold"
                                  : ""
                              }`}
                            >
                              {/* Option text */}
                              {opt.text && <span>{opt.text}</span>}

                              {/* Option image */}
                              {opt.type === "image" && opt.image_url && (
                                <img
                                  src={opt.image_url}
                                  alt={`Option ${opt.id}`}
                                  style={{
                                    maxWidth: "150px",
                                    display: "block",
                                    marginTop: "5px",
                                  }}
                                />
                              )}

                              {/* Label for correct option */}
                              {opt.id === q.correct_option_id && " (Correct)"}
                            </li>
                          ))}
                        </ul>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* USER TEST TABLE */}
      <div className="mt-5">
        <h4 className="mb-3">
          User Test Details
          <button
            type="button"
            className="btn primary-button text-white rounded-pill mx-4"
            onClick={() => handleReportSend(1,null)}
          >
            Send Reports ({selectedUsers.length})
          </button>
        </h4>

        <Table bordered striped hover responsive>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedUsers.length === exam.user_tests.length &&
                    exam.user_tests.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>User</th>
              <th>Email</th>
              <th>Completed On</th>
              <th>Completed</th>
              <th>Attempted</th>
              <th>Correct</th>
              <th>Flag</th>
              <th>Report Sent</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {exam.user_tests.map((u, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.user_id)}
                    onChange={() => handleSelectUser(u.user_id)}
                  />
                </td>
                <td>{u.user?.name}</td>
                <td>{u.user?.email}</td>
                <td>
                  {u.completed_on?.$date
                    ? new Date(u.completed_on.$date).toLocaleString()
                    : "-"}
                </td>
                <td>{u.test_completed ? "Yes" : "No"}</td>
                <td>{attemptedCount(u)}</td>
                <td>{correctCount(u)}</td>
                <td
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {u.is_terminated ? (
                    <>
                      <span>Test Terminated: {u.termination_reason}</span>

                      <button
                        title="Reset Test"
                        onClick={() => handleResetTest(u.user_id)}
                        style={{
                          background: "#dc3545",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 6px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FaSync color="#fff" />
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {
                    u?.test_report_sent ? "Sent": "Not Sent"
                  }
                </td>

                <td>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-email">Send Report</Tooltip>}
                  >
                    <button
                      type="button"
                      className="btn primary-button text-white rounded-pill mx-1"
                      onClick={() => handleReportSend(2,u.user_id)}
                    >
                      <EmailOutlined />
                    </button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ExamEditPage;
